import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, DollarSign, Calendar, CreditCard, Banknote, Bitcoin, ArrowRightLeft, Smartphone, FileText, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const FORMAS = [
  { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
  { value: 'pix', label: 'PIX', icon: Smartphone },
  { value: 'cartao_debito', label: 'Cartão Débito', icon: CreditCard },
  { value: 'cartao_credito', label: 'Cartão Crédito', icon: CreditCard },
  { value: 'transferencia', label: 'Transferência', icon: ArrowRightLeft },
  { value: 'boleto', label: 'Boleto', icon: FileText },
  { value: 'crypto', label: 'Criptomoeda', icon: Bitcoin },
];

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function FinalizarOSModal({ open, onOpenChange, ordem, onSuccess }) {
  const [pagamentos, setPagamentos] = useState([{ _key: 'init-0', forma: 'dinheiro', valor: '', observacao: '' }]);
  const [parcelas, setParcelas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastOrdemId, setLastOrdemId] = useState(null);

  const valorTotal = Number(ordem?.valor_total || 0);
  const genKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Inicializa pagamentos quando ordem muda (sem useEffect para evitar set-state-in-effect)
  if (open && ordem && ordem.id !== lastOrdemId) {
    setLastOrdemId(ordem.id);
    setPagamentos([{ _key: `init-${ordem.id}`, forma: 'dinheiro', valor: valorTotal.toFixed(2), observacao: '' }]);
    setParcelas([]);
  }

  const somaPagos = pagamentos.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const somaParcelas = parcelas.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const totalDistribuido = somaPagos + somaParcelas;
  const restante = valorTotal - totalDistribuido;

  const addPagamento = () => setPagamentos([...pagamentos, { _key: genKey(), forma: 'pix', valor: restante > 0 ? restante.toFixed(2) : '', observacao: '' }]);
  const removePagamento = (idx) => setPagamentos(pagamentos.filter((_, i) => i !== idx));

  const addParcela = () => {
    // calcula próxima data: 30 dias após última parcela ou hoje+30
    const ultima = parcelas[parcelas.length - 1];
    const baseDate = ultima ? new Date(ultima.data_vencimento + 'T00:00:00') : new Date();
    baseDate.setDate(baseDate.getDate() + 30);
    const dataVenc = baseDate.toISOString().slice(0, 10);
    setParcelas([...parcelas, { _key: genKey(), valor: restante > 0 ? restante.toFixed(2) : '', data_vencimento: dataVenc, forma_prevista: 'pix' }]);
  };
  const removeParcela = (idx) => setParcelas(parcelas.filter((_, i) => i !== idx));

  const dividirEmParcelas = (n) => {
    // limpa parcelas atuais e cria N parcelas iguais com o RESTANTE (valorTotal - somaPagos)
    const valorParcelar = Math.max(0, valorTotal - somaPagos);
    if (valorParcelar <= 0) {
      toast.error('Não há valor restante para parcelar (revise os pagamentos imediatos)');
      return;
    }
    const valorParcela = Math.floor((valorParcelar / n) * 100) / 100;
    const resto = +(valorParcelar - valorParcela * n).toFixed(2);
    const novas = [];
    for (let i = 0; i < n; i++) {
      const base = new Date();
      base.setDate(base.getDate() + 30 * (i + 1));
      novas.push({
        _key: genKey(),
        valor: (valorParcela + (i === 0 ? resto : 0)).toFixed(2),
        data_vencimento: base.toISOString().slice(0, 10),
        forma_prevista: 'pix',
      });
    }
    setParcelas(novas);
  };

  const salvar = async () => {
    if (Math.abs(restante) > 0.02) {
      toast.error(`A soma não bate com o total da OS. Restam ${fmtBRL(restante)}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pagamentos: pagamentos
          .filter((p) => parseFloat(p.valor) > 0)
          .map((p) => ({ forma: p.forma, valor: parseFloat(p.valor), observacao: p.observacao || null })),
        parcelas: parcelas
          .filter((p) => parseFloat(p.valor) > 0)
          .map((p) => ({ valor: parseFloat(p.valor), data_vencimento: p.data_vencimento, forma_prevista: p.forma_prevista || null })),
      };
      await api.post(`/ordens/${ordem.id}/finalizar`, payload);
      toast.success('OS concluída e pagamentos registrados!');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="finalizar-os-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" /> Concluir OS — Registrar Pagamento
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Valor total da OS: <span className="text-white font-semibold">{fmtBRL(valorTotal)}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Resumo */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
            <p className="text-[10px] uppercase text-emerald-300">Pago Agora</p>
            <p className="text-lg font-bold text-white">{fmtBRL(somaPagos)}</p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2">
            <p className="text-[10px] uppercase text-amber-300">A Receber</p>
            <p className="text-lg font-bold text-white">{fmtBRL(somaParcelas)}</p>
          </div>
          <div className={`border rounded p-2 ${Math.abs(restante) < 0.02 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <p className="text-[10px] uppercase text-slate-300">Restante</p>
            <p className={`text-lg font-bold ${Math.abs(restante) < 0.02 ? 'text-blue-300' : 'text-red-300'}`}>{fmtBRL(restante)}</p>
          </div>
        </div>

        {/* Pagamentos */}
        <div>
          <Label className="text-xs text-slate-400 uppercase flex items-center justify-between mb-1">
            <span>Pagamentos recebidos agora</span>
            <Button type="button" size="sm" variant="ghost" onClick={addPagamento} className="h-6 text-emerald-400 hover:bg-emerald-500/10 normal-case text-xs" data-testid="add-pagamento">
              <Plus className="w-3 h-3 mr-1" /> Forma
            </Button>
          </Label>
          {pagamentos.map((p, idx) => (
            <div key={p._key || idx} className="flex gap-2 mb-1.5">
              <Select value={p.forma} onValueChange={(v) => setPagamentos(pagamentos.map((x, i) => i === idx ? { ...x, forma: v } : x))}>
                <SelectTrigger className="w-44 bg-obsidian border-white/10 text-white text-sm" data-testid={`pagamento-forma-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-carbon border-white/10 text-white">
                  {FORMAS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" min="0" value={p.valor} onChange={(e) => setPagamentos(pagamentos.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))}
                placeholder="0,00" className="bg-obsidian border-white/10 text-white text-sm" data-testid={`pagamento-valor-${idx}`} />
              {pagamentos.length > 1 && (
                <Button type="button" size="sm" variant="ghost" onClick={() => removePagamento(idx)} className="text-red-400 hover:bg-red-500/10 px-2">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Parcelas */}
        <div className="mt-3">
          <Label className="text-xs text-slate-400 uppercase flex items-center justify-between mb-1">
            <span>Parcelas a receber (futuro)</span>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="ghost" onClick={() => dividirEmParcelas(2)} className="h-6 text-amber-400 hover:bg-amber-500/10 normal-case text-xs">2x</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => dividirEmParcelas(3)} className="h-6 text-amber-400 hover:bg-amber-500/10 normal-case text-xs">3x</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => dividirEmParcelas(4)} className="h-6 text-amber-400 hover:bg-amber-500/10 normal-case text-xs">4x</Button>
              <Button type="button" size="sm" variant="ghost" onClick={addParcela} className="h-6 text-amber-400 hover:bg-amber-500/10 normal-case text-xs" data-testid="add-parcela">
                <Plus className="w-3 h-3 mr-1" /> Parcela
              </Button>
            </div>
          </Label>
          {parcelas.length === 0 ? (
            <div className="text-xs text-slate-600 py-1.5">
              Se o valor já foi pago integralmente, deixe vazio. Senão clique &quot;2x/3x/4x&quot; para gerar parcelas automáticas com o restante.
            </div>
          ) : (
            parcelas.map((p, idx) => (
              <div key={p._key || idx} className="flex gap-2 mb-1.5">
                <Input type="number" step="0.01" min="0" value={p.valor}
                  onChange={(e) => setParcelas(parcelas.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))}
                  placeholder="Valor" className="bg-obsidian border-white/10 text-white text-sm flex-1" data-testid={`parcela-valor-${idx}`} />
                <Input type="date" value={p.data_vencimento}
                  onChange={(e) => setParcelas(parcelas.map((x, i) => i === idx ? { ...x, data_vencimento: e.target.value } : x))}
                  className="bg-obsidian border-white/10 text-white text-sm w-44" data-testid={`parcela-data-${idx}`} />
                <Button type="button" size="sm" variant="ghost" onClick={() => removeParcela(idx)} className="text-red-400 hover:bg-red-500/10 px-2">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>

        {Math.abs(restante) > 0.02 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-xs text-red-300 flex items-start gap-2 mt-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>A soma (pagamentos + parcelas) deve ser exatamente <strong>{fmtBRL(valorTotal)}</strong>. Restam <strong>{fmtBRL(restante)}</strong>.</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-white/5 mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Cancelar</Button>
          <Button onClick={salvar} disabled={saving || Math.abs(restante) > 0.02}
            className="bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50" data-testid="finalizar-os-confirmar">
            {saving ? 'Salvando...' : 'Concluir OS'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
